import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * The signed-in user's profile, as the hub's `toProfileResponse` returns it (`auth-controller.ts`,
 * `GET /auth/me` and `PUT /auth/profile`). Mirrors hub `data-models/profile.ts`.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). The hub never returns credentials, 2FA secrets or lockout state, so they are not modelled.
 */
export class Profile extends Base {
  // Writable fields (PUT /auth/profile)
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;

  // Read-only fields
  email?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  avatar?: string | null;
  username?: string | null;
  language?: string | null;
  timezone?: string | null;
  emailVerified?: boolean | null;
  is2FAEnabled?: boolean | null;
  onboardingCompleted?: boolean | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.readWritableFields(source);
    this.readHubFields(source);
  }

  private readWritableFields(source: Record<string, unknown>): void {
    this.firstName = nullableString(source['firstName']);
    this.lastName = nullableString(source['lastName']);
    this.phone = nullableString(source['phone']);
    this.dateOfBirth = nullableString(source['dateOfBirth']);
    this.address = nullableString(source['address']);
    this.city = nullableString(source['city']);
    this.state = nullableString(source['state']);
    this.zipCode = nullableString(source['zipCode']);
  }

  private readHubFields(source: Record<string, unknown>): void {
    this.email = nullableString(source['email']);
    this.latitude = nullableNumber(source['latitude']);
    this.longitude = nullableNumber(source['longitude']);
    this.avatar = nullableString(source['avatar']);
    this.username = nullableString(source['username']);
    this.language = nullableString(source['language']);
    this.timezone = nullableString(source['timezone']);
    this.emailVerified = nullableBoolean(source['emailVerified']);
    this.is2FAEnabled = nullableBoolean(source['is2FAEnabled']);
    this.onboardingCompleted = nullableBoolean(source['onboardingCompleted']);
  }
}
